import { Component, Input, OnInit } from '@angular/core';
import {
  DataStateChangeEvent,
  GridDataResult
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import {
  ExpensesService,
  GetExpenseDto,
  LinkExpenseCommand
} from '@api-accounting-client';
import { DrawerBaseComponent } from '@app/shared/bases';
import { toHttpDataSourceRequestString } from '@app/shared/helpers';
import { DialogExtendedService } from '@app/shared/services';
import { SideBarDataSharingService } from '@app/shared/services/side-bar';

@Component({
  selector: 'app-expense-link-drawer',
  templateUrl: './expense-link-drawer.component.html',
  styleUrls: ['./expense-link-drawer.component.scss'],
  standalone: false
})
export class ExpenseLinkDrawerComponent
  extends DrawerBaseComponent
  implements OnInit
{
  /**
   * UID of the purchase invoice to link the selected expense to.
   */
  @Input() invoiceUid: string;

  gridView: GridDataResult = { data: [], total: 0 };
  isLoading = false;
  isWaiting = false;
  selectedKeys: string[] = [];
  selectedExpense: GetExpenseDto | null = null;

  pageSize = 20;
  skip = 0;
  state: State = {
    skip: 0,
    take: 20,
    sort: [{ field: 'date', dir: 'desc' }],
    filter: { filters: [], logic: 'and' }
  };

  constructor(
    protected sideBarDataSharingService: SideBarDataSharingService,
    private expensesService: ExpensesService,
    private dialogExtendedService: DialogExtendedService
  ) {
    super(sideBarDataSharingService);
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.drawerWidth = 900;
    await this.loadUnlinkedExpenses();
  }

  private get dataSourceRequestString(): string {
    const state: State = {
      ...this.state,
      filter:
        this.state.filter?.filters?.length > 0 ? this.state.filter : null
    };
    return toHttpDataSourceRequestString(state);
  }

  async loadUnlinkedExpenses(): Promise<void> {
    this.isLoading = true;
    try {
      const result = await this.expensesService
        .expensesUnlinkedGet(this.dataSourceRequestString)
        .toPromise();
      this.gridView = {
        data: result?.items ?? [],
        total: result?.total ?? 0
      };
    } finally {
      this.isLoading = false;
    }
  }

  async onDataStateChange(state: DataStateChangeEvent): Promise<void> {
    this.state = {
      ...this.state,
      skip: state.skip,
      take: state.take,
      filter: state.filter ?? this.state.filter
    };
    this.skip = state.skip;
    this.selectedKeys = [];
    this.selectedExpense = null;
    await this.loadUnlinkedExpenses();
  }

  onSelectedKeysChange(keys: string[]): void {
    this.selectedKeys = keys ?? [];
    const uid = this.selectedKeys[0];
    this.selectedExpense = uid
      ? (this.gridView.data as GetExpenseDto[]).find((item) => item.uid === uid) ??
        null
      : null;
  }

  async onLink(): Promise<void> {
    if (!this.selectedExpense?.uid || !this.invoiceUid) {
      return;
    }

    this.isWaiting = true;
    const command: LinkExpenseCommand = {
      purchaseInvoiceUid: this.invoiceUid
    };

    try {
      await this.expensesService
        .expensesUidLinkPost(this.selectedExpense.uid, command)
        .toPromise();
      this.closeSideDrawer(this.selectedExpense.uid);
    } catch (error: any) {
      const message =
        error?.error?.detail ||
        error?.error?.title ||
        this.extractFirstValidationMessage(error?.error?.errors) ||
        'validationError';
      this.dialogExtendedService.showValidationDialog(message);
      this.selectedKeys = [];
      this.selectedExpense = null;
      await this.loadUnlinkedExpenses();
    } finally {
      this.isWaiting = false;
    }
  }

  private extractFirstValidationMessage(
    errors: Record<string, string[]> | undefined
  ): string | null {
    if (!errors) {
      return null;
    }
    const first = Object.values(errors).find(
      (msgs) => Array.isArray(msgs) && msgs.length > 0
    );
    return first?.[0] ?? null;
  }

  closeSideDrawer(expenseUid?: string): void {
    this.sideBarDataSharingService.closeComponent(expenseUid);
  }

  onActionClick(action: string): void {
    switch (action) {
      case 'Link':
        this.onLink();
        break;
      case 'Discard':
      case 'Cancel':
        this.closeSideDrawer();
        break;
    }
  }
}
